import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Badge, Card, Row, Col } from 'react-bootstrap';
import './ModalAyuda.css';
import { obtenerClaveRol } from './guiaAyudas';

function ModalAyuda({ show, onHide, guia, rolId, configuracion = {} }) {
  const [autoMostrar, setAutoMostrar] = useState(false);

  // Cargar preferencia guardada en localStorage
  useEffect(() => {
    const pref = localStorage.getItem('auto_mostrar_ayuda');
    setAutoMostrar(pref === 'true');
  }, [show]);

  // Manejar cambio en el checkbox/switch de auto-mostrar
  const handleToggleAutoMostrar = (e) => {
    const checked = e.target.checked;
    setAutoMostrar(checked);
    localStorage.setItem('auto_mostrar_ayuda', checked ? 'true' : 'false');
  };

  if (!guia) return null;

  const claveRol = obtenerClaveRol(rolId);
  const consejoRol = guia.consejosPorRol?.[claveRol] || guia.consejosPorRol?.estudiante || null;

  const colorPrimario = configuracion.color_primario || '#014751';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="modal-ayuda-custom fade"
    >
      <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="modal-ayuda-icon-box shadow-sm rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: `${colorPrimario}15`,
              color: colorPrimario,
              width: '54px',
              height: '54px',
              minWidth: '54px'
            }}
          >
            <i className={`fa-solid ${guia.icono || 'fa-circle-question'} fa-xl`}></i>
          </div>
          <div>
            <Badge
              bg="light"
              className="text-uppercase tracking-wide fw-bold border px-2 py-1 mb-1"
              style={{ color: colorPrimario, fontSize: '0.72rem' }}
            >
              Guía Contextual
            </Badge>
            <Modal.Title className="h5 fw-bold mb-0 text-dark">
              {guia.titulo}
            </Modal.Title>
            {guia.subtitulo && (
              <p className="text-muted small mb-0 mt-1">{guia.subtitulo}</p>
            )}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-3">
        {/* Resumen Principal */}
        <div
          className="p-3 rounded-3 mb-4 border-start border-4 shadow-sm"
          style={{
            backgroundColor: '#f8f9fa',
            borderColor: colorPrimario
          }}
        >
          <p className="mb-0 text-secondary leading-relaxed" style={{ fontSize: '0.94rem' }}>
            <i className="fa-solid fa-circle-info me-2" style={{ color: colorPrimario }}></i>
            {guia.resumen}
          </p>
        </div>

        {/* ESQUEMA LÓGICO DE INICIO (SI EXISTE - p.ej /Principal) */}
        {guia.pasosIniciales && guia.pasosIniciales.length > 0 && (
          <div className="mb-4 p-3 rounded-3 bg-light border shadow-sm">
            <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
              <i className="fa-solid fa-route" style={{ color: colorPrimario }}></i>
              Esquema Lógico para comenzar a usar el sistema:
            </h6>
            <Row className="g-2 align-items-center">
              {guia.pasosIniciales.map((pasoItem, idx) => (
                <Col key={idx} xs={12} sm={6} md={4} className="mb-2">
                  <Card className="h-100 border-0 shadow-sm rounded-3 bg-white p-2 text-center position-relative hover-card">
                    <Card.Body className="p-2 d-flex flex-column align-items-center justify-content-center">
                      <span
                        className="badge rounded-pill position-absolute top-0 start-0 m-2 px-2 py-1"
                        style={{ backgroundColor: colorPrimario, fontSize: '0.7rem' }}
                      >
                        Paso {pasoItem.paso}
                      </span>
                      <div
                        className="rounded-circle p-2 mb-2 d-flex align-items-center justify-content-center text-white"
                        style={{ backgroundColor: colorPrimario, width: '38px', height: '38px' }}
                      >
                        <i className={`fa-solid ${pasoItem.icono} fa-md`}></i>
                      </div>
                      <h6 className="fw-bold mb-1 text-dark small">{pasoItem.titulo}</h6>
                      <p className="text-muted small mb-0" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                        {pasoItem.descripcion}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* PROCESO DE CALIFICACIÓN (SI EXISTE - p.ej /CalificacionesGeneral) */}
        {guia.procesoCalificacion && guia.procesoCalificacion.length > 0 && (
          <div className="mb-4 p-3 rounded-3 bg-light border shadow-sm">
            <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
              <i className="fa-solid fa-diagram-project" style={{ color: colorPrimario }}></i>
              Flujo y Proceso de Calificaciones:
            </h6>
            <Row className="g-2 text-center">
              {guia.procesoCalificacion.map((paso, idx) => (
                <Col key={idx} xs={12} sm={6} md={3}>
                  <div className="p-2 bg-white rounded-3 border h-100 shadow-sm d-flex flex-column align-items-center justify-content-center">
                    <span
                      className="badge rounded-pill mb-2 px-2 py-1 text-uppercase"
                      style={{ backgroundColor: `${colorPrimario}20`, color: colorPrimario, fontSize: '0.68rem' }}
                    >
                      Paso {paso.paso}
                    </span>
                    <i className={`fa-solid ${paso.icono} fa-lg mb-2`} style={{ color: colorPrimario }}></i>
                    <strong className="d-block text-dark small mb-1">{paso.titulo}</strong>
                    <span className="text-muted small" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                      {paso.descripcion}
                    </span>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Funcionalidades Clave */}
        {guia.funcionalidades && guia.funcionalidades.length > 0 && (
          <div className="mb-4">
            <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
              <i className="fa-solid fa-star-of-life small" style={{ color: colorPrimario }}></i>
              Funcionalidades destacadas en esta pantalla
            </h6>
            <Row className="g-3">
              {guia.funcionalidades.map((item, idx) => (
                <Col md={6} key={idx}>
                  <Card className="h-100 border-0 shadow-sm rounded-3 bg-white hover-card p-2">
                    <Card.Body className="d-flex align-items-start gap-3 p-2">
                      <div
                        className="rounded-3 p-2 d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: `${colorPrimario}10`,
                          color: colorPrimario,
                          width: '38px',
                          height: '38px',
                          minWidth: '38px'
                        }}
                      >
                        <i className={`fa-solid ${item.icono || 'fa-check'} fa-lg`}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark" style={{ fontSize: '0.88rem' }}>
                          {item.titulo}
                        </h6>
                        <p className="text-muted mb-0" style={{ fontSize: '0.81rem', lineHeight: '1.35' }}>
                          {item.descripcion}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Consejo según Rol */}
        {consejoRol && (
          <div
            className="p-3 rounded-3 d-flex align-items-start gap-3 shadow-sm border"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f7f7 100%)',
              borderColor: `${colorPrimario}30`
            }}
          >
            <div
              className="rounded-circle p-2 d-flex align-items-center justify-content-center text-white mt-1"
              style={{ backgroundColor: colorPrimario, width: '32px', height: '32px', minWidth: '32px' }}
            >
              <i className="fa-solid fa-lightbulb small"></i>
            </div>
            <div>
              <span className="fw-bold text-dark d-block small mb-1">
                Recomendación para tu perfil:
              </span>
              <p className="text-secondary mb-0 small" style={{ lineHeight: '1.4' }}>
                {consejoRol}
              </p>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-between align-items-center">
        <Form.Check
          type="switch"
          id="auto-mostrar-ayuda-switch"
          label={
            <span className="text-muted small user-select-none">
              Auto-mostrar ayuda al ingresar a cada sección
            </span>
          }
          checked={autoMostrar}
          onChange={handleToggleAutoMostrar}
          className="d-flex align-items-center gap-2 custom-ayuda-switch"
        />

        <Button
          variant="primary"
          onClick={onHide}
          className="px-4 py-2 rounded-3 fw-bold border-0 shadow-sm"
          style={{
            backgroundColor: colorPrimario,
            borderColor: colorPrimario
          }}
        >
          <i className="fa-solid fa-check me-2"></i>
          Entendido
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalAyuda;
